import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState<Language>(() => {
    // First check URL path
    if (location.pathname.startsWith('/fr')) {
      return 'fr';
    }
    
    // Then check localStorage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      return savedLanguage as Language;
    }

    // Finally check browser language
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'fr' ? 'fr' : 'en';
  });

  // Check user's country on mount
  useEffect(() => {
    const checkUserLocation = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        if (data.country_code === 'FR' && language === 'en' && !location.pathname.startsWith('/fr')) {
          // Redirect French users to French version if they're not already there
          const newPath = '/fr' + location.pathname;
          navigate(newPath, { replace: true });
          setLanguage('fr');
        }
      } catch (error) {
        console.error('Error detecting user location:', error);
      }
    };

    checkUserLocation();
  }, []);

  // Handle language changes
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;

    // Update URL when language changes
    const currentPath = location.pathname;
    if (language === 'fr' && !currentPath.startsWith('/fr')) {
      navigate('/fr' + currentPath, { replace: true });
    } else if (language === 'en' && currentPath.startsWith('/fr')) {
      navigate(currentPath.replace('/fr', ''), { replace: true });
    }
  }, [language, location.pathname, navigate]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};